import React from "react";
import styled from "styled-components";
import { T } from "../../theme";
import { ECOSYSTEM } from "../../data/ecosystem";
import Reveal from "../Reveal";
import { Band, Shell, Eyebrow, Heading, Lead, Head } from "./ui";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  @media (max-width: 820px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

const Card = styled.article`
  position: relative;
  background: ${T.card};
  border: 1.5px solid ${T.border};
  border-radius: 18px;
  padding: 22px;
  overflow: hidden;
  transition: border-color 0.18s, box-shadow 0.18s, transform 0.16s;

  &::after {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: ${(p) => p.$c};
    opacity: 0.7;
    transition: opacity 0.18s;
  }

  &:hover {
    border-color: ${(p) => p.$c}55;
    box-shadow: 0 10px 34px rgba(0, 0, 0, 0.16);
    transform: translateY(-3px);
    &::after { opacity: 1; }
  }
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
`;

const Logo = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.92rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #fff;
  background: ${(p) => p.$c};
  box-shadow: 0 4px 14px ${(p) => p.$c}55;
`;

const LogoImg = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  flex-shrink: 0;
  object-fit: cover;
  background: #fff;
  box-shadow: 0 4px 14px ${(p) => p.$c}55;
`;

const Name = styled.h3`
  font-size: 0.98rem;
  font-weight: 800;
  color: ${T.text};
  letter-spacing: -0.01em;
`;

const Seg = styled.p`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${T.muted2};
  margin-top: 2px;
`;

const Desc = styled.p`
  font-size: 0.83rem;
  color: ${T.muted};
  line-height: 1.6;
  margin-bottom: 14px;
`;

const Build = styled.p`
  font-size: 0.78rem;
  color: ${T.muted};
  line-height: 1.55;
  padding: 11px 13px;
  background: ${T.card2};
  border-radius: 10px;
  margin-bottom: 14px;

  b { color: ${T.text}; font-weight: 700; }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Tag = styled.span`
  font-size: 0.68rem;
  font-weight: 600;
  color: ${T.muted};
  padding: 3px 9px;
  border-radius: 999px;
  background: ${T.card2};
  border: 1px solid ${T.border};
`;

const SoonBadge = styled.span`
  position: absolute;
  top: 18px;
  right: 18px;
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.28);
  padding: 3px 8px;
  border-radius: 999px;
`;

export default function Ecosystem() {
  return (
    <Band id="empresas" $soft $top $bottom aria-labelledby="eco-title">
      <Shell>
        <Reveal>
          <Head>
            <Eyebrow>O ecossistema</Eyebrow>
            <Heading id="eco-title">Uma vaga. Seis empresas para crescer.</Heading>
            <Lead>
              A Guebly é uma holding de tecnologia. Ao entrar, você ganha acesso a frentes
              que vão de fintech a games — e pode evoluir entre elas conforme sua carreira pede.
            </Lead>
          </Head>
        </Reveal>

        <Grid>
          {ECOSYSTEM.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 3) * 70}>
              <Card $c={c.color}>
                {c.status === "em_desenvolvimento" && <SoonBadge>Em breve</SoonBadge>}
                <Top>
                  {c.logo ? (
                    <LogoImg
                      $c={c.color}
                      src={c.logo}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                    />
                  ) : (
                    <Logo $c={c.color} aria-hidden="true">{c.initials}</Logo>
                  )}
                  <div>
                    <Name>{c.name}</Name>
                    <Seg>{c.segment}</Seg>
                  </div>
                </Top>
                <Desc>{c.desc}</Desc>
                <Build><b>Você constrói:</b> {c.build}</Build>
                <Tags>
                  {c.tags.map((t) => <Tag key={t}>{t}</Tag>)}
                </Tags>
              </Card>
            </Reveal>
          ))}
        </Grid>
      </Shell>
    </Band>
  );
}
